import React from "react";
import styled from "@emotion/styled";
import { Box, Typography, CircularProgress } from "@mui/material";
import ClockIcon from "src/icons/ClockIcon";
import { useTheme } from "@mui/material/styles";
import { BANNER_VARIANTS, EXTEND_BUTTON_TOOLTIP } from "./ExtendPreviewBanner.consts";
import HyroButton from "@hyro-components/hyro-button/HyroButton";
import Tooltip from "@hyro-components/Tooltip";
import { BUTTON_VARIANTS, BUTTON_COLORS, BUTTON_SIZES } from "@hyro-components/hyro-button/HyroButton.consts";

const PreviewBanner = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.tertiary[100],
  border: `1px solid ${theme.palette.secondary[400]}`,
  borderRadius: "8px",
  padding: "12px 16px",
  marginBottom: "18px",
  width: "100%",
  position: "relative",
}));

const BannerContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== "variant"
})(({ variant }) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: variant === BANNER_VARIANTS.HEADER ? "center" : "space-between",
  gap: variant === BANNER_VARIANTS.HEADER ? "12px" : "0",
  width: "100%",
}));

const BannerTextContent = styled(Box, {
  shouldForwardProp: (prop) => prop !== "variant"
})(({ variant }) => ({
  display: "flex",
  alignItems: "center",
  gap: variant === BANNER_VARIANTS.HEADER ? "8px" : "12px",
  flex: variant === BANNER_VARIANTS.HEADER ? "0 0 auto" : 1
}));

const BannerTextWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "variant"
})(({ variant }) => ({
  display: "flex",
  flexWrap: variant === BANNER_VARIANTS.HEADER ? "nowrap" : "wrap",
  alignItems: "center",
  gap: variant === BANNER_VARIANTS.HEADER ? "8px" : "6px",
  flex: variant === BANNER_VARIANTS.HEADER ? "0 0 auto" : 1,
  whiteSpace: variant === BANNER_VARIANTS.HEADER ? "nowrap" : "normal"
}));

const BannerText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text[800],
  whiteSpace: "nowrap"
}));

const BannerTimeText = styled(Typography)(({ theme }) => ({
  color: theme.palette.text[700],
  whiteSpace: "nowrap"
}));

const ExtendPreviewBanner = ({ getExpirationText, handleExtendPreview, isExtendingPreview, variant = BANNER_VARIANTS.CONTAINED }) => {
  const theme = useTheme();

  const content = (
    <BannerContent variant={variant}>
      <BannerTextContent variant={variant}>
        <ClockIcon color={theme.palette.secondary.main} size={20} />
        <BannerTextWrapper variant={variant}>
          <BannerText variant="subtitle2">Preview expires in</BannerText>
          <BannerTimeText variant="body2">{getExpirationText()}</BannerTimeText>
        </BannerTextWrapper>
      </BannerTextContent>
      <Tooltip tooltipStyle={{ whiteSpace: "pre-line" }} title={EXTEND_BUTTON_TOOLTIP}>
        <HyroButton
          variant={BUTTON_VARIANTS.OUTLINED}
          color={BUTTON_COLORS.PRIMARY}
          size={BUTTON_SIZES.SMALL}
          onClick={handleExtendPreview}
          disabled={isExtendingPreview}
          sx={{ minWidth: "70px" }}
        >
          {isExtendingPreview ? <CircularProgress size={14} color="inherit" /> : "Extend"}
        </HyroButton>
      </Tooltip>
    </BannerContent>
  );

  if (variant === BANNER_VARIANTS.HEADER) {
    return content;
  }

  return <PreviewBanner>{content}</PreviewBanner>;
};

export default ExtendPreviewBanner;
